import {
  CcFeeMode,
  CcFeePolicyAdmin,
  CcFeePolicyPassThroughView,
  CcFeePolicySource,
} from '../../../../apis/types'

const MODE_LABELS: Record<CcFeeMode, string> = {
  NONE: 'None',
  FLAT_PER_TXN: 'Flat per transaction',
  PERCENT: 'Percent of payment',
  PASS_THROUGH: 'Pass-through (company rate)',
}

export function ccFeeChargingStatus(charging: boolean): 'CHARGING' | 'NOT_CHARGING' {
  return charging ? 'CHARGING' : 'NOT_CHARGING'
}

export function ccFeeModeLabel(mode: CcFeeMode | null | undefined): string {
  if (mode == null) return '—'
  return MODE_LABELS[mode] ?? mode
}

function formatUsd(amount: string | null | undefined): string | null {
  if (amount == null || amount === '') return null
  const n = Number(amount)
  if (!Number.isFinite(n)) return amount
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Percentage points: 2.21 → 2.21%. Do not multiply by 100. Keep the API string. */
function formatPercentPoints(percent: string | null | undefined): string | null {
  if (percent == null || percent === '') return null
  const n = Number(percent)
  if (!Number.isFinite(n)) return percent
  return `${percent}%`
}

function ccFeeDateRangeLabel(startDate: string | null | undefined, endDate: string | null | undefined): string | null {
  if ((startDate == null || startDate === '') && (endDate == null || endDate === '')) {
    return null
  }
  return `${ccFeeDateLabel(startDate)} to ${ccFeeDateLabel(endDate)}`
}

function formatFlavorFlat(amount: string | null | undefined): string | null {
  if (amount == null || amount === '') return null
  const n = Number(amount)
  if (Number.isFinite(n) && n === 0) return null
  return formatUsd(amount)
}

function formatFlavorFormula(percent: string | null | undefined, flatAmount: string | null | undefined): string | null {
  const pct = formatPercentPoints(percent)
  const flat = formatFlavorFlat(flatAmount)
  if (pct != null && flat != null) return `${pct} + ${flat}`
  if (pct != null) return pct
  if (flat != null) return flat
  return null
}

/** Credit and debit formulas from the in-force pass-through row, e.g. 2.21% credit, 1.25% + $0.25 debit. */
function formatPassThroughFormulas(passThrough: CcFeePolicyPassThroughView | null | undefined): string | null {
  if (passThrough == null) return null
  const parts: string[] = []
  const credit = formatFlavorFormula(passThrough.creditPercent, passThrough.creditFlatAmount)
  const debit = formatFlavorFormula(passThrough.debitPercent, passThrough.debitFlatAmount)
  if (credit != null) parts.push(`${credit} credit`)
  if (debit != null) parts.push(`${debit} debit`)
  return parts.length === 0 ? null : parts.join(', ')
}

export function ccFeeAmountLabel(policy: CcFeePolicyAdmin | null | undefined): string {
  const mode = policy?.mode ?? 'NONE'
  if (mode === 'FLAT_PER_TXN') {
    return formatUsd(policy?.flatAmount) ?? '—'
  }
  if (mode === 'PERCENT') {
    return formatPercentPoints(policy?.percent) ?? '—'
  }
  if (mode === 'PASS_THROUGH') {
    return formatPassThroughFormulas(policy?.passThrough) ?? 'Company rate unset'
  }
  return '—'
}

export function ccFeeCapLabel(amount: string | null | undefined): string {
  return formatUsd(amount) ?? '—'
}

/** Calendar day stored at UTC midnight. Format in UTC so PT does not show the prior day. */
export function ccFeeDateLabel(isoDate: string | null | undefined): string {
  if (isoDate == null || isoDate === '') return '—'
  const day = isoDate.slice(0, 10)
  const parts = day.split('-').map(Number)
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return isoDate
  const [year, month, date] = parts
  return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function ccFeeSourceLabel(
  source: CcFeePolicySource | null | undefined,
  tesaVersion: string | null | undefined
): string {
  if (source === 'TESA_SCHEDULE') {
    return tesaVersion != null && tesaVersion !== '' ? `TESA schedule (${tesaVersion})` : 'TESA schedule'
  }
  if (source === 'ACCOUNT_OVERRIDE') {
    return tesaVersion != null && tesaVersion !== '' ? `Account override (TESA ${tesaVersion})` : 'Account override'
  }
  return '—'
}

export function ccFeePanelDescription(): string {
  return 'Fee policy applied to card payments on this account'
}

function isRateUnset(policy: CcFeePolicyAdmin): boolean {
  const amount = ccFeeAmountLabel(policy)
  return amount === '—' || amount === 'Company rate unset'
}

export function ccFeeChargingHint(policy: CcFeePolicyAdmin | null | undefined): string {
  if (policy?.charging === true) {
    if (isRateUnset(policy)) {
      return 'Policy mode is set, but no rate is configured.'
    }
    return 'This policy applies to card payments.'
  }
  if (policy?.policyCuid == null) {
    return 'Not charging. No policy in force.'
  }
  if (policy.mode === 'NONE') {
    return policy.reason != null && policy.reason !== ''
      ? 'Not charging.'
      : 'Not charging. No fee on card payments.'
  }
  return 'Not charging.'
}

export function ccFeeScheduleLabel(policy: CcFeePolicyAdmin | null | undefined): string {
  const schedule = policy?.schedule
  if (schedule == null) return '—'
  const parts = [`TESA ${schedule.tesaVersion}`, ccFeeModeLabel(schedule.mode)]
  if (schedule.mode === 'FLAT_PER_TXN' && schedule.flatAmount != null) {
    parts.push(formatUsd(schedule.flatAmount) ?? schedule.flatAmount)
  }
  if (schedule.mode === 'PERCENT') {
    const percent = formatPercentPoints(policy?.percent)
    if (percent != null) {
      parts.push(percent)
    }
  }
  if (schedule.mode === 'PASS_THROUGH') {
    const formulas = formatPassThroughFormulas(policy?.passThrough)
    if (formulas != null) {
      parts.push(formulas)
    }
  }
  const cap = formatUsd(schedule.monthlyCapAmount)
  if (cap != null) {
    parts.push(`cap ${cap}`)
  }
  const window = ccFeeDateRangeLabel(schedule.startDate, schedule.endDate)
  if (window != null) {
    parts.push(window)
  }
  return parts.join(', ')
}

export function ccFeePassThroughLabel(policy: CcFeePolicyAdmin | null | undefined): string {
  if (policy?.passThrough == null) return '—'
  const formulas = formatPassThroughFormulas(policy.passThrough) ?? 'Company rate unset'
  const window = ccFeeDateRangeLabel(policy.passThrough.startDate, policy.passThrough.endDate)
  return window == null ? formulas : `${formulas}, ${window}`
}
